import {ModelDestroyOptions, ModelSaveOptions} from 'backbone';

import {CodonTranslationTable} from 'biojs/BioConstants';
import {Translation as BioJSTranslation, TranslationAttributes} from 'biojs/Translation';
import {Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import BaseTranslation from 'molbio/translations/backbone/BaseTranslation';
import Translations from 'molbio/translations/backbone/Translations';
import {
  getTranslationRegionGroupForTranslation,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';

type HoistedMethod<A extends keyof BioJSTranslation> = ReturnType<BioJSTranslation[A]>;
type HoistedAttribute<A extends keyof TranslationAttributes> = TranslationAttributes[A];

class Translation extends BaseTranslation {
  initialize(): void {
    this.listenTo(
      this.get('_translation'),
      'destroy remove',
      (model: BioJSTranslation, collection: Translations, options: ModelDestroyOptions) => {
        this.selfDestroy(options);
      }
    );
    this.listenTo(this.get('_translation'), 'change', this.change.bind(this));

    // When the _translation gets saved on the server, update the wrapper to have the id as well.
    this.listenTo(this.get('_translation'), 'change:id', () => this.set({id: this.get('_translation').id}));
  }

  change(model: BioJSTranslation, options: ModelSaveOptions): void {
    this.trigger('change:_translation', this, this.collection, options);
  }

  start(): HoistedAttribute<'start'> {
    return this.get('_translation').start();
  }

  end(): HoistedAttribute<'end'> {
    return this.get('_translation').end();
  }

  uid(): string {
    return this.get('_translation').cid;
  }

  _translation(): BioJSTranslation {
    return this.get('_translation');
  }

  strand(): StrandOfDoubleStranded {
    return this.get('_translation').strand();
  }

  editable(): HoistedMethod<'editable'> {
    return this.get('_translation').editable();
  }

  fromSubPart(): HoistedMethod<'fromSubPart'> {
    return this.get('_translation').fromSubPart();
  }

  translationGroupId(): HoistedAttribute<'translationGroupId'> {
    return this.get('_translation').translationGroupId();
  }

  codonTranslationTable(sequence: Sequence): CodonTranslationTable {
    return this.getTranslationRegionGroup(sequence)?.codonTranslationTable ?? CodonTranslationTable.STANDARD;
  }

  coerceStartCodonToMethionine(sequence: Sequence): boolean {
    return this.getTranslationRegionGroup(sequence)?.coerceStartCodonToMethionine ?? false;
  }

  selfDestroy(options?: ModelDestroyOptions): void {
    this.trigger('destroy', this, this.collection, options);
  }

  destroy(): HoistedMethod<'destroy'> {
    const resp = this.get('_translation').destroy();
    this.selfDestroy();
    return resp;
  }

  getTranslationRegionGroup(sequence: Sequence): TranslationRegionGroupData | undefined {
    return getTranslationRegionGroupForTranslation(sequence, this);
  }

  toReactDict(): TranslationAttributes {
    return {
      start: this.start(),
      end: this.end(),
      strand: this.strand(),
      translationGroupId: this.translationGroupId(),
      id: this._translation().id,
      fromSubPart: this.fromSubPart(),
    };
  }
}

export default Translation;
